import {
  Calendar,
  Clock,
  DollarSign,
  FileText,
  Phone,
  Wrench,
} from 'lucide-react';

import { IndustryPage, type IndustryPageConfig } from '@/components/marketing/industry-page';

const config: IndustryPageConfig = {
  seoTitle: 'AI Receptionist for Trades & Contractors — Capture Quote Requests 24/7',
  seoDescription:
    'Stop losing jobs because you missed a call on the tools. Our AI receptionist for trades businesses captures quote requests, answers FAQs, and books call-backs — while you work.',
  path: '/industries/trades',
  badge: 'Trades & Contractors',
  headline: 'Answer the 6pm emergency call while you\'re on a ladder.',
  subheadline:
    'Built for HVAC, plumbing and electrical. Your AI receptionist answers every call, books the job into your calendar, and transfers real emergencies to you — so the next company on Google never gets it.',
  pains: [
    'Customers call while you\'re on a roof or under a house — they leave no message and call someone else.',
    'You lose evenings returning calls that could have been handled while they were fresh.',
    'Repeat questions about call-out fees and service areas eat into productive time.',
  ],
  features: [
    {
      icon: Phone,
      title: 'Answers on the first ring',
      desc: 'Every call picked up, even mid-job. No more missed calls going to a competitor.',
    },
    {
      icon: Calendar,
      title: 'Quote request capture',
      desc: 'Captures customer name, contact number, job description, and suburb. Ready for you to review and quote when you\'re done on site.',
    },
    {
      icon: Wrench,
      title: 'Service area & FAQ answers',
      desc: 'Call-out fees, service areas, job types you do and don\'t take — answered from your knowledge base, accurately, every time.',
    },
    {
      icon: DollarSign,
      title: 'Qualify leads before callback',
      desc: 'Filter out out-of-area jobs and out-of-scope requests before you spend time calling back.',
    },
    {
      icon: Clock,
      title: 'After-hours job capture',
      desc: 'Customers ring at 7pm when they discover a leak. Your AI answers, captures the details, and you quote first thing tomorrow.',
    },
    {
      icon: FileText,
      title: 'Job inquiry log',
      desc: 'All inquiries with contact details and job summaries in one dashboard. No sticky notes, no lost callbacks.',
    },
  ],
  faqs: [
    {
      q: 'Will it work when I\'m in an area with poor signal?',
      a: 'Yes — the AI handles the call on your behalf. Calls route through the cloud, not your mobile, so patchy coverage on site doesn\'t matter.',
    },
    {
      q: 'Can it tell customers my call-out fees and service area?',
      a: 'Yes — add your rates, service suburbs, and job types to your knowledge base. The agent answers accurately from that content.',
    },
    {
      q: 'What if a customer has an emergency (burst pipe, gas leak)?',
      a: 'Configure an emergency transfer rule. The agent recognises urgency and transfers the call to your mobile immediately, with context.',
    },
    {
      q: 'I only want after-hours answering — can I set that up?',
      a: 'Yes — configure the agent to activate only outside business hours, or full time. Your choice.',
    },
    {
      q: 'How much does it cost compared to a call answering service?',
      a: 'Significantly less than a human answering service. See our pricing page for current plans.',
    },
  ],
  ctaHeadline: 'Stop losing jobs to voicemail.',
  ctaSub: 'Set up in 10 minutes. Works while you\'re on the tools.',
};

export function TradesIndustryPage(): JSX.Element {
  return <IndustryPage config={config} />;
}
